import {
  Button,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  HelpIcon,
  Page,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { ratingApi } from 'apis/rating';
import { Language, languages } from 'app/utils/supportedLanguages';
import { AppLink } from 'components/appLink';
import {
  HookFormSelectionList,
  HookFormText,
  HookFormTextarea,
} from 'components/hookForm';
import { LandingCsatPreview } from 'components/settings/components/sections/satisfaction/components/common/LandingCsatPreview';
import { ThankYouPreview } from 'components/settings/components/sections/satisfaction/components/common/ThankYouPreview';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import style from './index.module.scss';

type FieldValues = {
  ratingQuestion: string;
  commentQuestion?: string;
  placeholderText: string;
  lookAndFeel: Satisfaction.RatingStyle[];
  thankYouMessage: string;
  buttonText: string;
  body: string;
};

export const SurveyCustomizationLinkView = (): JSX.Element => {
  const navigate = useAppNavigate();

  const notify = useNotifications();

  const linkPreview =
    'https://csat.reviews/?ratingId=XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX';
  const gsmCharactersRegex = new RegExp(
    '^[A-Za-z0-9 \\r\\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!"#$%&\'()*+,\\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\\\\\[~\\]|\u20AC]*$',
  );
  const reservedGap = 3;

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    control,
    trigger,
    formState: { isValid, isSubmitting },
  } = useForm<FieldValues>({
    mode: 'all',
  });

  const { settingsId, languageCode } = useAppPathParams(
    '/settings/satisfaction/phone/survey/sms/:settingsId/:languageCode',
  );

  const handleNavigateBackToSurvey = useCallback(() => {
    navigate({
      path: `/settings/satisfaction/phone/survey/:settingsId`,
      params: { path: { settingsId } },
    });
  }, [navigate, settingsId]);

  const language: Language | undefined = useMemo(
    () => languages.filter((l) => l.code == languageCode).shift(),
    [languageCode],
  );

  useEffect(() => {
    // If no language is defined we don't allow to continue further with customizting the language settings
    if (!language) {
      handleNavigateBackToSurvey();
    }
  }, [handleNavigateBackToSurvey, language]);

  const [createRatingSettings] = ratingApi.useLazyCreateRatingSettingsQuery();

  const [updateRatingSettings] = ratingApi.useLazyUpdateRatingSettingsQuery();

  const [
    readRatingSettings,
    {
      ratingSettings: readRatingSettingsResponse,
      isSuccess: readRatingSettingsIsSuccess,
    },
  ] = ratingApi.useLazyReadRatingSettingsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      ratingSettings: data?.data,
    }),
  });

  const [
    getSurveyRatingSettings,
    { data: surveyRatingSettings, isSuccess: surveySettingIsSuccess },
  ] = ratingApi.useLazyGetRatingSettingsQuery();

  const ratingStyleFormValue: Satisfaction.RatingStyle[] = useWatch({
    control: control,
    name: 'lookAndFeel',
    defaultValue: ['emojis'],
  });

  /**
   * For some reason, casting CSATCustomization to SmsCustomization fails and
   * returns an undefined object. This is a workaround to convert the content
   * back to Json and parse it back.
   */
  function withSmsCustomization(
    customization: Satisfaction.CSATCustomization,
    handleSmsCustomization: (t: Satisfaction.LinkRatingCustomization) => void,
  ): void | undefined {
    try {
      const smsCustomization: Satisfaction.LinkRatingCustomization =
        customization as Satisfaction.LinkRatingCustomization;
      return handleSmsCustomization(smsCustomization);
    } catch {
      return undefined;
    }
  }

  useEffect(() => {
    getSurveyRatingSettings({ ratingSettingsId: settingsId });
  }, [settingsId]);

  const fetchLanguageRatingSettings = useCallback(() => {
    readRatingSettings({
      language: languageCode,
      parentId: settingsId,
      level: 'language',
      surveyType: surveyRatingSettings?.data.survey_type ?? 'text_to_speech',
    });
  }, [readRatingSettings, surveySettingIsSuccess]);

  useEffect(() => {
    if (surveyRatingSettings?.data) {
      fetchLanguageRatingSettings();
    }
  }, [surveyRatingSettings]);

  useEffect(() => {
    if (readRatingSettingsIsSuccess && readRatingSettingsResponse) {
      const customization =
        readRatingSettingsResponse.rating_settings.length > 0
          ? readRatingSettingsResponse.rating_settings[0].customization
          : readRatingSettingsResponse.default_rating_settings?.customization;

      if (customization) {
        withSmsCustomization(customization, (smsCustomization) => {
          setValue(
            'ratingQuestion',
            smsCustomization.rating_question as string,
          );
          setValue(
            'commentQuestion',
            smsCustomization?.comment_question as string,
          );
          setValue(
            'thankYouMessage',
            smsCustomization?.thank_you_message as string,
          );
          setValue(
            'placeholderText',
            smsCustomization?.placeholder_text as string,
          );
          setValue('buttonText', smsCustomization?.button_text as string);
          setValue(
            'lookAndFeel',
            smsCustomization?.look_and_feel
              ? [smsCustomization?.look_and_feel]
              : ratingStyleFormValue,
          );
          setValue('body', smsCustomization?.body ?? '');
        });

        trigger();
      }
    }
  }, [
    readRatingSettingsResponse,
    readRatingSettingsIsSuccess,
    setValue,
    trigger,
  ]);

  const handleFormSubmit = async (values: FieldValues) => {
    try {
      // Checks the fetched language settings, if they already exited it performs an update, if it did not it creates one.
      const smsCustomization: Satisfaction.LinkRatingCustomization = {
        rating_question: values.ratingQuestion,
        comment_question: values.commentQuestion,
        placeholder_text: values.placeholderText,
        look_and_feel: ratingStyleFormValue.length
          ? ratingStyleFormValue[0]
          : 'emojis',
        thank_you_message: values.thankYouMessage,
        button_text: values.buttonText,
        body: values.body,
      };
      if (readRatingSettingsResponse?.rating_settings.length) {
        const updateReq: Satisfaction.UpdateSmsSettingsParams = {
          id: readRatingSettingsResponse?.rating_settings[0].id,
          customization: smsCustomization,
        };
        updateRatingSettings(updateReq);
      } else {
        await createRatingSettings({
          language: languageCode,
          level: 'language',
          parent_id: settingsId,
          survey_type: 'link',
          customization: smsCustomization,
        });
        // After creating, we fetch the settings again, which means that next time the language
        // settings object will be present so it will perform an update instead of create
        fetchLanguageRatingSettings();
      }
      notify(
        {
          title: 'Success',
          text: `The survey customization has been saved.`,
          icon: 'done',
          time: 5000,
        },
        NotificationType.Success,
      );

      reset({}, { keepValues: true });

      navigate({
        path: `/settings/satisfaction/phone/survey/:settingsId`,
        params: { path: { settingsId } },
      });
    } catch (error) {
      notify(
        {
          title: 'Error while saving the survey customization.',
        },
        NotificationType.Error,
      );
    }
  };

  const handleValidSubmit: SubmitHandler<FieldValues> = async (values) => {
    await handleFormSubmit(values);
  };

  const bodyValue = watch('body') ?? '';

  return (
    <>
      <form noValidate tabIndex={-1} onSubmit={handleSubmit(handleValidSubmit)}>
        <Page.Section
          titleMeta={
            <AppLink
              path="/settings/satisfaction/phone/survey/:settingsId"
              params={{ path: { settingsId } }}
            >
              ← Back to language options
            </AppLink>
          }
          controls={
            <Button
              variant="primary"
              isLoading={isSubmitting}
              disabled={!isValid || isSubmitting}
              type="submit"
            >
              Save
            </Button>
          }
        >
          <FlexBox flexDirection="row" gap="500">
            {surveyRatingSettings?.data?.survey_type && (
              <FlexBox flexDirection="column" gap="100">
                <Text.Paragraph variant="1" subdued>
                  Survey type
                </Text.Paragraph>

                <Text.Paragraph variant="1">
                  {Satisfaction.prettySurveyType(
                    surveyRatingSettings?.data.survey_type,
                  )}
                </Text.Paragraph>
              </FlexBox>
            )}
            <FlexBox flexDirection="column" gap="100">
              <Text.Paragraph variant="1" subdued>
                CSAT survey name
              </Text.Paragraph>

              {surveySettingIsSuccess && surveyRatingSettings?.data && (
                <Text.Paragraph variant="1">
                  {surveyRatingSettings?.data.name}
                </Text.Paragraph>
              )}
            </FlexBox>

            <FlexBox flexDirection="column" gap="100">
              <Text.Paragraph variant="1" subdued>
                Language
              </Text.Paragraph>

              <Text.Paragraph variant="1">{language?.label}</Text.Paragraph>
            </FlexBox>
          </FlexBox>
        </Page.Section>
        <Page.Section title="SMS Message">
          <FormGrid>
            <FormField span={6}>
              <FlexBox gap="100" padding="200 0">
                <Text.Paragraph variant="2">
                  This is the text message which will be sent in the SMS
                  (encoded by GSM standard).
                </Text.Paragraph>
                <HelpIcon
                  text="GSM-7 is a character encoding standard which packs the most commonly used letters and symbols.
            GSM-7 encoded SMS messages can carry up to 160 characters."
                  dark
                />
              </FlexBox>
              <div className={style.root}>
                <FlexBox flexDirection="column">
                  <FieldWrapper label="Preview">
                    <div className={style.preview}>
                      <div className={style.message}>
                        {bodyValue + ' '}
                        <span className={style.link}>{linkPreview}</span>
                      </div>
                    </div>
                  </FieldWrapper>
                </FlexBox>
                <FlexBox flexDirection="column">
                  <HookFormTextarea
                    name="body"
                    label="Message"
                    control={control}
                    rules={{
                      required: 'SMS message is required',
                      maxLength: {
                        value: 90,
                        message: `Reached limit of 160 characters`,
                      },
                      pattern: {
                        value: gsmCharactersRegex,
                        message: 'Only GSM characters are allowed',
                      },
                    }}
                    componentProps={{
                      placeholder: 'Hi, please rate your experience with us',
                    }}
                  />
                  <FlexBox gap="100" padding="200 0">
                    <Text.Paragraph variant="4">
                      {bodyValue.length + reservedGap + linkPreview.length}/160
                      characters
                    </Text.Paragraph>
                    <HelpIcon
                      text="70 characters are occupied for the generated CSAT link"
                      dark
                    />
                  </FlexBox>
                </FlexBox>
              </div>
            </FormField>
          </FormGrid>
        </Page.Section>

        <Page.Section
          title="Thank you and follow up question"
          titleMeta={
            <HelpIcon
              text="This is a preview of the landing page where the user can add a
              comment."
              dark
            />
          }
        >
          <FlexBox flexDirection="column" gap="300">
            <div className={style.root}>
              <LandingCsatPreview
                thankYouText={watch('ratingQuestion')}
                questionText={watch('commentQuestion') ?? ''}
                placeholderText={watch('placeholderText')}
                submitButtonText={watch('buttonText')}
                ratingStyle={ratingStyleFormValue[0]}
              />

              <FlexBox flexDirection="column" style={{ width: '100%' }}>
                <HookFormText
                  name="ratingQuestion"
                  label="Question"
                  control={control}
                  rules={{
                    required: 'Question is required',
                  }}
                  componentProps={{
                    placeholder: 'How do you feel about the service?',
                  }}
                />

                <HookFormText
                  name="commentQuestion"
                  label="Follow up question"
                  control={control}
                  componentProps={{
                    placeholder: 'Would you like to share any comments?',
                  }}
                />

                <HookFormText
                  name="placeholderText"
                  label="Placeholder"
                  control={control}
                  componentProps={{
                    placeholder: 'Share your thoughts...',
                  }}
                />

                <HookFormText
                  name="buttonText"
                  label="Button text"
                  control={control}
                  componentProps={{
                    placeholder: 'Send feedback',
                  }}
                />

                <HookFormSelectionList
                  name="lookAndFeel"
                  label="Rating style"
                  style={{ width: '200px' }}
                  control={control}
                  componentProps={{
                    placeholder: 'Select style',
                    getItemTitleByValue: (style) => (
                      <Text.Paragraph>{style}</Text.Paragraph>
                    ),
                  }}
                >
                  {['stars', 'emojis', 'numbers'].map((ratingStyle) => (
                    <SelectionList.Item key={ratingStyle} value={ratingStyle}>
                      <Text.Paragraph>{ratingStyle}</Text.Paragraph>
                    </SelectionList.Item>
                  ))}
                </HookFormSelectionList>
              </FlexBox>

              <ThankYouPreview
                thankYouText={watch('thankYouMessage') ?? ''}
                ratingStyle={
                  ratingStyleFormValue.length
                    ? ratingStyleFormValue[0]
                    : 'emojis'
                }
              />

              <FlexBox
                gap={'400'}
                flexDirection="column"
                style={{ width: '100%' }}
              >
                <HookFormText
                  name="thankYouMessage"
                  label="Thank you text"
                  control={control}
                  rules={{
                    required: 'Thank you message is required',
                  }}
                  componentProps={{
                    placeholder: 'Thank you for your feedback!',
                  }}
                />
              </FlexBox>
            </div>
          </FlexBox>
        </Page.Section>
      </form>
    </>
  );
};
